/* ══════════════════════════════════════════════════════════
   Contractor Application Form — single-page logic
   ══════════════════════════════════════════════════════════
   Responsibilities:
     - Field-level validation (8 fields)
     - Optional CRM settings load (brand colors)
     - Submit → {proxy}/api/locator/applications
   Submit contract (unchanged backend):
     owner_name = first + " " + last, business_name = company,
     specialties = [] (now optional server-side).
   ══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  const root = document.getElementById('contractorApplication');
  if (!root) return;

  // ─── Constants ────────────────────────────────────────
  const STORE_KEY = root.dataset.storeKey || 'torchstar';
  const PROXY_URL = (root.dataset.proxyUrl || '').replace(/\/$/, '');

  const REQUIRED = [
    'first_name',
    'last_name',
    'company',
    'email',
    'phone',
    'address_zip',
    'service_radius_miles',
  ];

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

  // ─── State ────────────────────────────────────────────
  const state = { submitting: false };

  const form = root.querySelector('[data-ca-form]');
  const submitBtn = root.querySelector('[data-ca-submit]');
  const submitLabel = root.querySelector('[data-ca-submit-label]');
  const submitSpinner = root.querySelector('[data-ca-submit-spinner]');
  const successEl = root.querySelector('[data-ca-success]');

  // ─────────────────────────────────────────────────────────────
  // 1. Load CRM settings (brand colors only)
  // ─────────────────────────────────────────────────────────────
  async function loadSettings() {
    if (!PROXY_URL) return;
    try {
      const resp = await fetch(`${PROXY_URL}/api/locator/${encodeURIComponent(STORE_KEY)}/settings`, {
        headers: { Accept: 'application/json' },
      });
      if (!resp.ok) return;
      applySettings(await resp.json());
    } catch (err) {
      console.warn('[ContractorApplication] settings fetch failed:', err);
    }
  }

  function applySettings(settings) {
    if (!settings || typeof settings !== 'object') return;

    if (settings.primary_color) {
      const primary = settings.primary_color;
      root.style.setProperty('--ca-primary', primary);
      root.style.setProperty('--ca-primary-hover', darkenColor(primary, 0.22));
      const tint = hexToRgba(primary, 0.06);
      const tint2 = hexToRgba(primary, 0.12);
      if (tint) root.style.setProperty('--ca-primary-tint', tint);
      if (tint2) root.style.setProperty('--ca-primary-tint-2', tint2);
    }
    if (settings.text_color && isValidHex(settings.text_color)) {
      root.style.setProperty('--ca-ink', settings.text_color);
    }
    if (settings.background_color && isValidHex(settings.background_color)) {
      root.style.setProperty('--ca-bg', settings.background_color);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 2. Validation
  // ─────────────────────────────────────────────────────────────
  function getValue(name) {
    const el = root.querySelector(`[data-ca-field="${name}"]`);
    return el ? (el.value || '').trim() : '';
  }

  function validateField(name, silent = false) {
    const input = root.querySelector(`[data-ca-field="${name}"]`);
    if (!input) return true;

    const value = (input.value || '').trim();
    let error = '';

    if (REQUIRED.includes(name) && !value) {
      error = 'Required';
    } else if (name === 'email' && value) {
      if (!EMAIL_RE.test(value)) error = 'Enter a valid email';
      else if (value.length > 160) error = 'Email too long';
    } else if (name === 'phone' && value) {
      const digits = value.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) error = 'Enter a valid phone number';
    } else if (name === 'address_zip' && value) {
      if (!US_ZIP_RE.test(value)) error = 'Enter a valid ZIP code';
    } else if (name === 'service_radius_miles' && value) {
      const n = parseInt(value, 10);
      if (isNaN(n) || n < 5 || n > 200) error = 'Enter a value between 5 and 200';
    } else if (name === 'first_name' && value.length > 60) {
      error = 'Too long (max 60)';
    } else if (name === 'last_name' && value.length > 60) {
      error = 'Too long (max 60)';
    } else if (name === 'company' && value.length > 120) {
      error = 'Too long (max 120)';
    }

    if (!silent) {
      if (error) {
        showFieldError(name, error);
      } else {
        clearFieldError(name);
        if (value) input.classList.add('is-valid');
      }
    }
    return !error;
  }

  function validateAll() {
    let allValid = true;
    for (const name of REQUIRED) {
      if (!validateField(name)) allValid = false;
    }
    return allValid;
  }

  function showFieldError(name, msg) {
    const errEl = root.querySelector(`[data-ca-error="${name}"]`);
    const input = root.querySelector(`[data-ca-field="${name}"]`);
    if (errEl) {
      errEl.textContent = msg;
      errEl.classList.add('is-shown');
    }
    if (input) {
      input.classList.add('is-invalid');
      input.classList.remove('is-valid');
    }
  }

  function clearFieldError(name) {
    const errEl = root.querySelector(`[data-ca-error="${name}"]`);
    const input = root.querySelector(`[data-ca-field="${name}"]`);
    if (errEl) {
      errEl.textContent = '';
      errEl.classList.remove('is-shown');
    }
    if (input) {
      input.classList.remove('is-invalid');
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 3. Form data collection — maps the 8 fields to the backend payload
  // ─────────────────────────────────────────────────────────────
  function collectFormData() {
    const first = getValue('first_name');
    const last = getValue('last_name');

    const data = {
      source_store: STORE_KEY,
      business_name: getValue('company'),
      owner_name: (first + ' ' + last).trim(),
      email: getValue('email'),
      phone: getValue('phone'),
      service_radius_miles: parseInt(getValue('service_radius_miles'), 10) || 25,
      address_zip: getValue('address_zip'),
      address_country: 'US',
      specialties: [],
    };

    const honeypot = root.querySelector('[data-ca-honeypot]');
    if (honeypot && honeypot.value) {
      data.honeypot_value = honeypot.value;
    }

    return data;
  }

  // ─────────────────────────────────────────────────────────────
  // 4. Submit
  // ─────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (state.submitting) return;

    // Agreement must be checked (guards against Enter submitting past the disabled button)
    const agreeCb = root.querySelector('[data-ca-agree]');
    const agreeOk = !agreeCb || agreeCb.checked;
    const agreeErr = root.querySelector('[data-ca-error="agree"]');
    if (!agreeOk && agreeErr) {
      agreeErr.textContent = 'Please accept the Terms and Privacy Policy to continue';
      agreeErr.classList.add('is-shown');
    }

    if (!validateAll() || !agreeOk) {
      const firstError = root.querySelector('.is-invalid, [data-ca-error="agree"].is-shown');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          if (typeof firstError.focus === 'function') firstError.focus();
        }, 200);
      }
      return;
    }

    state.submitting = true;
    submitBtn.disabled = true;
    if (submitLabel) submitLabel.textContent = 'Submitting...';
    if (submitSpinner) submitSpinner.hidden = false;

    try {
      const payload = collectFormData();
      const resp = await fetch(`${PROXY_URL}/api/locator/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (resp.status === 429) {
        showToast('Too many submissions. Please try again in an hour.', 'error');
        resetSubmitButton();
        return;
      }
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        let msg = 'Submission failed. Please try again.';
        try {
          const parsed = JSON.parse(errText);
          if (parsed?.data?.errors?.length) msg = parsed.data.errors.join('; ');
          else if (parsed?.statusMessage) msg = parsed.statusMessage;
        } catch {}
        showToast(msg, 'error');
        resetSubmitButton();
        return;
      }

      showSuccessState(payload.email);
    } catch (err) {
      console.error('[ContractorApplication] submit error:', err);
      showToast('Network error. Please check your connection and try again.', 'error');
      resetSubmitButton();
    }
  }

  function resetSubmitButton() {
    state.submitting = false;
    submitBtn.disabled = false;
    if (submitLabel) submitLabel.textContent = 'Submit Application';
    if (submitSpinner) submitSpinner.hidden = true;
  }

  function showSuccessState(email) {
    if (form) form.hidden = true;

    const emailEl = root.querySelector('[data-ca-success-email]');
    if (emailEl && email) emailEl.textContent = email;

    if (successEl) {
      successEl.hidden = false;
      successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // ─────────────────────────────────────────────────────────────
  // 5. Toast
  // ─────────────────────────────────────────────────────────────
  function showToast(message, type = 'info') {
    let toast = document.querySelector('.ca-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ca-toast';
      toast.style.cssText = `
        position: fixed;
        top: 24px;
        left: 50%;
        transform: translateX(-50%) translateY(-120%);
        background: #0f172a;
        color: #fff;
        padding: 14px 22px;
        border-radius: 10px;
        font-family: 'Manrope', sans-serif;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 12px 32px -8px rgba(15,23,42,0.4);
        z-index: 10000;
        transition: transform 0.35s cubic-bezier(0.22, 0.61, 0.36, 1);
        max-width: 440px;
        text-align: center;
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.background = type === 'error' ? '#dc2626' : '#0f172a';
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(-50%) translateY(0)';
    });
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(-120%)';
    }, 4500);
  }

  // ─────────────────────────────────────────────────────────────
  // 6. Color helpers (for CRM-driven brand color)
  // ─────────────────────────────────────────────────────────────
  function isValidHex(hex) {
    return typeof hex === 'string' && /^#?([a-f\d]{3}|[a-f\d]{6})$/i.test(hex);
  }
  function hexToRgba(hex, alpha) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return null;
    const r = parseInt(m[1], 16);
    const g = parseInt(m[2], 16);
    const b = parseInt(m[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  function darkenColor(hex, amount) {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return hex;
    const factor = 1 - amount;
    const r = Math.max(0, Math.round(parseInt(m[1], 16) * factor));
    const g = Math.max(0, Math.round(parseInt(m[2], 16) * factor));
    const b = Math.max(0, Math.round(parseInt(m[3], 16) * factor));
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
  }

  // ─────────────────────────────────────────────────────────────
  // 7. Wire up
  // ─────────────────────────────────────────────────────────────
  function init() {
    loadSettings();

    form.querySelectorAll('[data-ca-field]').forEach((el) => {
      const name = el.dataset.caField;
      el.addEventListener('blur', () => {
        if ((el.value || '').trim() || REQUIRED.includes(name)) {
          validateField(name);
        }
      });
      el.addEventListener('input', () => {
        if (el.classList.contains('is-invalid')) {
          clearFieldError(name);
        }
      });
    });

    // Agreement checkbox gates the submit button (must be ticked to enable)
    const agreeCb = root.querySelector('[data-ca-agree]');
    if (agreeCb && submitBtn) {
      const syncAgree = () => {
        submitBtn.disabled = !agreeCb.checked;
        const e = root.querySelector('[data-ca-error="agree"]');
        if (e) { e.textContent = ''; e.classList.remove('is-shown'); }
      };
      agreeCb.addEventListener('change', syncAgree);
      syncAgree();
    }

    form.addEventListener('submit', handleSubmit);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
